import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 背景画像 */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.readdy.ai/image/5b3c9bf2cc89003aa9f6224022a3d624/48bde31785e638a096f020c757324487.jpeg"
          alt="タレント 菊田あや子"
          className="w-full h-full object-cover object-top"
          loading="eager"
          decoding="async"
        />
        {/* より強い半透明オーバーレイ */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
      </div>

      {/* ナビゲーション */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex flex-col">
                <span className={`text-base sm:text-xl font-bold whitespace-nowrap transition-colors duration-300 leading-tight ${
                  isScrolled ? 'text-[#1e3a8a]' : 'text-white'
                }`} style={{ textShadow: isScrolled ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.5)' }}>
                  旅行会社向け 集客・広報支援サービス
                </span>
                <span className={`text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                  isScrolled ? 'text-gray-600' : 'text-white/90'
                }`} style={{ textShadow: isScrolled ? 'none' : '0 2px 6px rgba(0, 0, 0, 0.5)' }}>
                  タレント 菊田あや子 × SNS発信
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-6">
              {/* PC版のみ表示：お問い合わせボタン */}
              <button
                onClick={scrollToContact}
                className="hidden lg:block bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] text-white px-5 py-2.5 rounded-full text-base font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                お問い合わせはこちら
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* メインコンテンツ */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-32 text-center w-full">
        {/* 最上段（プレミアム・ヘッドライン）- PC版のみ太字 */}
        <div className="flex justify-center mb-6 sm:mb-8 lg:mb-10 animate-fade-in-up px-8 sm:px-12">
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-white font-extrabold lg:font-black tracking-wide whitespace-nowrap" style={{ textShadow: '0 3px 12px rgba(0, 0, 0, 0.7), 0 2px 6px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.4)' }}>
            旅行会社向け 戦略的集客パートナー
          </p>
        </div>

        {/* メイン（大見出し）- 核心メッセージ、PC版で赤文字を1-2行に調整 */}
        <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 sm:mb-8 lg:mb-12 leading-tight sm:leading-tight md:leading-tight lg:leading-tight animate-fade-in-up px-4 sm:px-6 max-w-6xl mx-auto" style={{ animationDelay: '0.1s', textShadow: '0 4px 12px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4)' }}>
          既存の広告では届かない<br className="hidden sm:block" />
          <span className="text-red-500 text-2xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl" style={{ textShadow: '0 4px 16px rgba(0, 0, 0, 0.7), 0 2px 6px rgba(0, 0, 0, 0.5)' }}>「アクティブな50代以上の女性層」</span><br className="hidden sm:block" />
          を動かす
        </h1>

        {/* 下段（中見出し）- 誰が提供するか */}
        <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-white font-bold mb-8 sm:mb-10 lg:mb-12 animate-fade-in-up px-4 sm:px-6" style={{ animationDelay: '0.2s', textShadow: '0 4px 12px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4)' }}>
          タレント菊田あや子 × SNS発信
        </p>

        {/* 補足説明 */}
        <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/95 mb-6 sm:mb-8 leading-relaxed lg:leading-relaxed max-w-5xl mx-auto font-medium animate-fade-in-up px-4 sm:px-6" style={{ animationDelay: '0.3s', textShadow: '0 3px 10px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4)' }}>
          信頼されるナビゲーターと、SNS専門チームが連携。貴社のツアーの魅力を50代以上の女性層へダイレクトに届け、集客・広報を強力にサポートします。
        </p>

        {/* お問い合わせボタン */}
        <div className="mb-12 sm:mb-16 lg:mb-20 animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
          <button
            onClick={scrollToContact}
            className="bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] text-white px-8 sm:px-10 lg:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg lg:text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}
          >
            お問い合わせはこちら
          </button>
        </div>

        {/* スクロールインジケーター */}
        <div className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 animate-float">
          <div className="w-6 h-10 sm:w-8 sm:h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white/70 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
